import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Target, Eye, FileText, CheckCircle2 } from "lucide-react";
import { breadcrumbSchema } from "@/components/Seo";
import PageSeo from "@/components/PageSeo";
import PageHeader from "@/components/public/PageHeader";
import { Reveal } from "@/components/public/Primitives";
import { getTrust } from "@/lib/api";

export default function CharitableTrust() {
  const { data: trust } = useQuery({ queryKey: ["trust"], queryFn: getTrust });
  if (!trust) return null;

  return (
    <div data-testid="charitable-trust-page">
      <PageSeo
        slug="charitable-trust"
        title={`${trust.name || "Charitable Trust"} | Girish Kumar Naidu`}
        description={trust.mission}
        keywords="Girish Kumar Naidu charitable trust, Bengaluru charitable trust, social welfare"
        image={trust.image}
        schemas={[
          { "@context": "https://schema.org", "@type": "Organization", name: trust.name, description: trust.mission },
          breadcrumbSchema([{ name: "Home", url: window.location.origin }, { name: "Charitable Trust" }]),
        ]}
      />
      <PageHeader
        overline="Charitable Trust"
        title={trust.name}
        subtitle={trust.mission}
        crumbs={[{ name: "Charitable Trust" }]}
      />

      {/* Mission / Vision */}
      <section className="bg-white py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-2 gap-10 items-center">
          <Reveal>
            <div className="relative">
              <div className="absolute -top-4 -left-4 bottom-4 right-4 bg-navy rounded-3xl" aria-hidden="true" />
              <div className="relative rounded-3xl overflow-hidden aspect-[4/3] border border-gray-200 shadow-2xl">
                <img src={trust.image} alt={trust.name} className="h-full w-full object-cover" />
              </div>
            </div>
          </Reveal>
          <div className="space-y-6">
            <Reveal>
              <div className="p-7 rounded-2xl bg-paper border border-gray-200">
                <Target className="h-7 w-7 text-gold mb-3" />
                <h3 className="font-display text-xl font-black text-navy mb-2">Our Mission</h3>
                <p className="text-gray-600 leading-relaxed">{trust.mission}</p>
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="p-7 rounded-2xl bg-paper border border-gray-200">
                <Eye className="h-7 w-7 text-civic mb-3" />
                <h3 className="font-display text-xl font-black text-navy mb-2">Our Vision</h3>
                <p className="text-gray-600 leading-relaxed">{trust.vision}</p>
              </div>
            </Reveal>
            <Reveal delay={0.2}>
              <div className="p-5 rounded-2xl bg-navy text-slate-200 flex items-start gap-3">
                <FileText className="h-5 w-5 text-gold shrink-0 mt-0.5" />
                <p className="text-sm">{trust.registration}</p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Impact */}
      {trust.impact?.length > 0 && (
        <section className="bg-navy py-16 grain">
          <div className="max-w-5xl mx-auto px-5 md:px-8 grid grid-cols-3 gap-6 text-center">
            {trust.impact.map((im, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <p className="font-display text-3xl md:text-5xl font-black text-gold">{im.value}</p>
                <p className="mt-2 font-mono text-[10px] md:text-xs text-slate-300 uppercase tracking-[0.15em]">{im.label}</p>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      {/* Activities */}
      <section className="bg-paper py-16 md:py-24 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <h2 className="font-display text-2xl md:text-3xl font-black text-navy mb-10 tracking-tight">Key Activities & Programs</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {(trust.activities || []).map((a, i) => (
              <Reveal key={i} delay={(i % 3) * 0.06}>
                <div className="flex items-start gap-3 bg-white rounded-2xl p-6 border border-gray-200 card-lift h-full">
                  <CheckCircle2 className="h-5 w-5 text-civic shrink-0 mt-0.5" />
                  <p className="text-gray-700 font-medium">{a}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Trustees */}
      {trust.trustees?.length > 0 && (
        <section className="bg-white py-16 md:py-24">
          <div className="max-w-7xl mx-auto px-5 md:px-8">
            <h2 className="font-display text-2xl md:text-3xl font-black text-navy mb-10 tracking-tight">Trustees & Team</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {trust.trustees.map((tr, i) => (
                <Reveal key={i} delay={(i % 3) * 0.08}>
                  <div className="bg-paper rounded-2xl p-7 border border-gray-200 text-center card-lift">
                    <div className="h-16 w-16 rounded-2xl bg-navy text-gold flex items-center justify-center mx-auto mb-4 font-display text-xl font-black">
                      {tr.name?.charAt(0)}
                    </div>
                    <p className="font-display font-black text-navy text-lg">{tr.name}</p>
                    <p className="font-mono text-[11px] uppercase tracking-wider text-gold mt-1">{tr.role}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
